import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import Input from "../../../../../components/Input";
import Text from "../../../../../components/Text";
import { PatientProcedureCode } from "../../../../../services";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
} from "../../../../../utils/utils";
import { ERA_PROCEDURE_COLUMNS } from "./constants";
import { CptAmounts, computeEraCptAmounts, sumEraCptAmounts } from "./era-cpt";

/**
 * Procedure sub-table for the ERA detail page ONLY.
 *
 * The ERA get API returns the payer's raw figures but leaves allowedAmount,
 * contractAdjustment, otherAdjustment, remainingBalance, patientResponsibility
 * and postingStatus null until the line is posted — so this table computes them
 * (see computeEraCptAmounts) and the post payload sends exactly what it shows.
 *
 * Every other screen renders a claim whose lines have already been posted and
 * therefore carry those values, and uses EraProcedureSubTable, which maps them
 * straight from the payload with no maths. Do not use this component there.
 */

const fmt = (n: number | null) => `$ ${(n ?? 0).toFixed(2)}`;

const cellSx = { py: 1, px: 1 };

// Field-level denial fields the user can edit on the ERA posting screen.
export type CptDenialField = "denied" | "denialCode" | "comments";

// When a CPT is marked Denied the payment figures are disabled — greyed and
// visually de-emphasised so only Denial Code / Comments / Post remain in play.
const deniedAmountSx = { color: "text.disabled", opacity: 0.6 };

const denialFieldSx = {
  "& .MuiInputBase-input": { ...DEFAULT_FONTSIZE, padding: "6px 8px" },
  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
};

const headerSx = {
  bgcolor: "grey.100",
  "& th": {
    fontSize: DEFAULT_FONTSIZE,
    fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
    py: 1.2,
    px: 1,
    borderBottom: "1px solid",
    borderColor: "grey.300",
    whiteSpace: "nowrap",
  },
};

const extractCpt = (pc: PatientProcedureCode) => {
  const obj = pc.procedureCode ?? {};
  const entry = (Object.values(obj)?.[0] as string) ?? "";
  const [cpt = ""] = entry.split(" - ");
  return cpt.trim() || (Object.keys(obj)?.[0] ?? "-");
};

interface EraComputedProcedureSubTableProps {
  procedures: PatientProcedureCode[];
  // Claim adjustment group code (e.g. "CO", "OA"). Splits a CPT's adjustment
  // into Contract (CO group) vs Other (everything else).
  groupCode?: string;
  // When provided, renders a per-CPT "Post" button.
  onPostCpt?: (pcIndex: number) => void;
  // Disables every CPT post button (e.g. missing permission).
  postDisabled?: boolean;
  // ECRH-5475 — when provided, renders the editable Comments / Denied / Denial
  // Code fields at CPT level and reports edits back to the ERA detail page.
  onEditCpt?: (
    pcIndex: number,
    field: CptDenialField,
    value: boolean | string
  ) => void;
}

const EraComputedProcedureSubTable = ({
  procedures,
  groupCode,
  onPostCpt,
  postDisabled = false,
  onEditCpt,
}: EraComputedProcedureSubTableProps) => {
  const showPostColumn = typeof onPostCpt === "function";
  const showDenialColumns = typeof onEditCpt === "function";

  if (!procedures?.length) {
    return (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: "1px dashed",
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      >
        <Text variant="body2" color="text.secondary">
          No procedure lines for this claim.
        </Text>
      </Box>
    );
  }

  const rows = procedures.map((pc) => computeEraCptAmounts(pc, groupCode));
  const totals: CptAmounts = sumEraCptAmounts(procedures, groupCode);

  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
      }}
    >
      <Table
        size="small"
        sx={{
          "& td, & th": { borderColor: "grey.200" },
        }}
      >
        <TableHead>
          <TableRow sx={headerSx}>
            {ERA_PROCEDURE_COLUMNS.map((c) => (
              <TableCell key={c.key}>{c.label}</TableCell>
            ))}
            {showDenialColumns && <TableCell>Comments</TableCell>}
            {showDenialColumns && <TableCell align="center">Denied</TableCell>}
            {showPostColumn && <TableCell align="center">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {procedures.map((pc, pcIndex) => {
            const a = rows[pcIndex];
            const denied = Boolean(pc.denied);
            const isPosted = Boolean(pc.posted);
            // ECRH-5475 — when denied, payment figures are disabled (greyed).
            const amountSx = denied ? deniedAmountSx : undefined;

            return (
              <TableRow
                key={pc.uuid ?? `${extractCpt(pc)}-${pcIndex}`}
                sx={{
                  verticalAlign: "top",
                  "& td": {
                    ...cellSx,
                    borderBottom: "1px solid",
                    borderColor: "grey.200",
                  },
                }}
              >
                <TableCell>{extractCpt(pc)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.bill)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.insurancePaid)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.allowed)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.deduct)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.coIns)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.coPay)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.patientResp)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.contractAdj)}</TableCell>
                <TableCell sx={amountSx}>{fmt(a.otherAdj)}</TableCell>
                {/* Balance column hidden from ERA procedure tables. */}
                {/* <TableCell sx={amountSx}>{fmt(a.balance)}</TableCell> */}
                {/* Payer adjudication status (Paid / Denied). The posting state
                    lives in the Action column (Post / Posted). */}
                <TableCell>
                  <Text
                    variant="body2"
                    sx={{ color: denied ? "#B42318" : "#1E7A3C" }}
                  >
                    {denied ? "Denied" : "Paid"}
                  </Text>
                </TableCell>
                {/* ECRH-5475 — editable Comments + Denied / Denial Code. */}
                {showDenialColumns && (
                  <TableCell>
                    <Input
                      textFieldProps={{
                        multiline: true,
                        minRows: 2,
                        value: pc.comments ?? "",
                        onChange: (e) =>
                          onEditCpt?.(pcIndex, "comments", e.target.value),
                        sx: { width: "100%" },
                        InputProps: { readOnly: isPosted },
                      }}
                    />
                  </TableCell>
                )}
                {showDenialColumns && (
                  <TableCell align="left">
                    <Checkbox
                      size="small"
                      checked={denied}
                      disabled={isPosted}
                      onChange={(e) =>
                        onEditCpt?.(pcIndex, "denied", e.target.checked)
                      }
                    />
                    {denied && (
                      <Input
                        textFieldProps={{
                          size: "small",
                          placeholder: "Denial Code",
                          value: pc.denialCode ?? "",
                          onChange: (e) =>
                            onEditCpt?.(pcIndex, "denialCode", e.target.value),
                          sx: { width: "100%", ...denialFieldSx },
                          InputProps: { readOnly: isPosted },
                        }}
                      />
                    )}
                  </TableCell>
                )}
                {showPostColumn && (
                  <TableCell align="center">
                    <StyledButtonV2
                      label={isPosted ? "Posted" : "Post"}
                      variant="outlined"
                      disabled={isPosted || postDisabled}
                      onClick={() => onPostCpt?.(pcIndex)}
                      sx={{
                        minWidth: 72,
                        height: 26,
                        borderRadius: "16px",
                        ...DEFAULT_FONTSIZE,
                        ...(isPosted && {
                          bgcolor: "transparent",
                          color: "grey.600",
                          borderColor: "grey.400",
                          "&.Mui-disabled": {
                            bgcolor: "transparent",
                            color: "grey.600",
                            borderColor: "grey.400",
                          },
                        }),
                      }}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}

          <TableRow
            sx={{
              "& td": {
                ...cellSx,
                fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                bgcolor: "grey.50",
                borderTop: "1px solid",
                borderColor: "grey.300",
              },
            }}
          >
            <TableCell>Total</TableCell>
            <TableCell>{fmt(totals.bill)}</TableCell>
            <TableCell>{fmt(totals.insurancePaid)}</TableCell>
            <TableCell>{fmt(totals.allowed)}</TableCell>
            <TableCell>{fmt(totals.deduct)}</TableCell>
            <TableCell>{fmt(totals.coIns)}</TableCell>
            <TableCell>{fmt(totals.coPay)}</TableCell>
            <TableCell>{fmt(totals.patientResp)}</TableCell>
            <TableCell>{fmt(totals.contractAdj)}</TableCell>
            <TableCell>{fmt(totals.otherAdj)}</TableCell>
            {/* Balance column hidden from ERA procedure tables. */}
            {/* <TableCell>{fmt(totals.balance)}</TableCell> */}
            <TableCell />
            {showDenialColumns && <TableCell />}
            {showDenialColumns && <TableCell />}
            {showPostColumn && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EraComputedProcedureSubTable;
